import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';

import { ProjectCollection } from 'app/modules/project/models/project-collection.model';
import { SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { FieldStatistics, FieldStatisticsPayload } from 'app/providers/field-statistics.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { CalculationRequest } from 'app/providers/storage-api/predictive.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { Constants } from '../../../../../constants/constants';
import { Utils } from '../../../../../utils/utils';
import { DriverAnalysisStep } from '../driver-analysis-dialog.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-driver-questions-step',
  templateUrl: './driver-questions-step.component.html',
  styleUrls: ['./driver-questions-step.component.scss'],
  standalone: false,
})
export class DriverQuestionsStepComponent implements DriverAnalysisStep, OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() data: Partial<CalculationRequest>;
  @Input() collection: ProjectCollection;
  @Input() fieldStatisticsPayload: FieldStatisticsPayload;

  @Output() onValidityChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  isLoading = true;

  displayedColumns = ['question', 'conversion'];
  dataSource = new MatTableDataSource<{ label; value }>();
  limits = { minQuestions: 0, maxQuestions: 0 };
  fieldStatistics: { [field: string]: FieldStatistics };

  constructor(private semantriaAccount: SemantriaAccount) {}

  async ngOnInit() {
    const account = await this.semantriaAccount.sub.latest();

    this.fieldStatistics = this.fieldStatisticsPayload.custom;

    this.limits.minQuestions =
      account.storageLimits['DRIVER_ANALYSIS_DRIVER_QUESTION_SIZE_MIN'] || 3;
    this.limits.maxQuestions =
      account.storageLimits['DRIVER_ANALYSIS_DRIVER_QUESTION_SIZE_MAX'] || 20;

    const availableQuestionFields = Object.keys(this.fieldStatistics)
      .filter(
        (fieldName) =>
          FieldService.isRating(fieldName) &&
          FieldService.isVisible(fieldName) &&
          fieldName !== this.data.dependentColumn &&
          Math.round(Utils.conversionRateFor(fieldName, this.fieldStatistics)) >
            Constants.MIN_ALLOWED_CONVERSION_RATE
      )
      .map((fieldName) => ({ label: this.getLongLabel(fieldName), value: fieldName }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.dataSource.data = availableQuestionFields;

    this.emitValidityChange();
    this.isLoading = false;
  }

  isAllQuestionsChecked() {
    return this.data.driverColumns?.length === this.dataSource.data.length;
  }

  isQuestionChecked(fieldName) {
    return this.data.driverColumns?.includes(fieldName);
  }

  onAllQuestionsToggled(checked) {
    this.data.driverColumns ??= [];

    if (checked) {
      this.data.driverColumns = this.dataSource.data.map((c) => c.value);
    } else {
      this.data.driverColumns = [];
    }

    this.emitValidityChange();
  }

  onQuestionToggled(checked, fieldName) {
    this.data.driverColumns ??= [];

    if (checked) {
      this.data.driverColumns.push(fieldName);
    } else {
      this.data.driverColumns = this.data.driverColumns.filter((col) => col !== fieldName);
    }

    this.emitValidityChange();
  }

  public getLongLabel(field: string): string {
    return FieldService.getFieldLongName(field);
  }

  private emitValidityChange() {
    const numSelected = this.data.driverColumns?.length || 0;
    this.onValidityChange.emit(
      numSelected >= this.limits.minQuestions && numSelected <= this.limits.maxQuestions
    );
  }
}
